import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DateTimePicker } from "@/components/DateTimePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Coins, LogOut, Plus, ShieldOff, ShieldCheck, KeyRound, RefreshCw, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { SUPERADMIN_PATH } from "@/App";

const fmtDT = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } };

export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", brand_name: "", brand_slug: "" });
  const [resetPw, setResetPw] = useState({ admin: null, pw: "" });
  const [showCode, setShowCode] = useState({}); // {admin_id: {code, rotated_at, visible}}
  const [myCode, setMyCode] = useState({ code: "", rotated_at: "", visible: false });
  const [newSaCode, setNewSaCode] = useState("");
  const [delAdmin, setDelAdmin] = useState(null); // admin obj
  const [delConfirm, setDelConfirm] = useState("");

  const deleteAdmin = async () => {
    if (!delAdmin) return;
    if (delConfirm.trim() !== delAdmin.brand_slug) { toast.error("Brand slug does not match"); return; }
    try {
      const { data } = await api.delete(`/superadmin/admins/${delAdmin.id}`);
      toast.success(`Admin '${delAdmin.username}' deleted (${data.deleted_users || 0} users removed)`);
      setDelAdmin(null); setDelConfirm("");
      refetchAdmins();
    } catch (err) { toast.error(formatError(err)); }
  };

  const load = async () => {
    try {
      const [{ data: adminsData }, { data: codeData }] = await Promise.all([
        api.get("/superadmin/admins"),
        api.get("/superadmin/login-code"),
      ]);
      setAdmins(adminsData);
      setMyCode((c) => ({ ...c, code: codeData.code, rotated_at: codeData.rotated_at }));
    } catch (e) { toast.error(formatError(e)); }
  };
  // Mutations on admins never change the superadmin's own login code — refresh admins only.
  const refetchAdmins = async () => {
    try { const { data } = await api.get("/superadmin/admins"); setAdmins(data); } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/superadmin/admins", form);
      toast.success(`Admin created. Access code: ${data.login_code || "(check admin row)"}`);
      setForm({ username: "", password: "", brand_name: "", brand_slug: "" });
      refetchAdmins();
    } catch (err) { toast.error(formatError(err)); }
  };

  const setActive = async (a, active) => {
    // Activation requires a "valid until" date/time; open the dialog instead.
    if (active) {
      setActivateModal({ admin: a, until: "" });
      return;
    }
    try { await api.post(`/superadmin/admins/${a.id}/set-active`, { active: false }); toast.success("Deactivated"); refetchAdmins(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const [activateModal, setActivateModal] = useState(null); // { admin, until }
  const submitActivate = async () => {
    if (!activateModal) return;
    if (!activateModal.until) { toast.error("Pick a date and time"); return; }
    const iso = new Date(activateModal.until).toISOString();
    if (new Date(iso) <= new Date()) { toast.error("Date must be in the future"); return; }
    try {
      await api.post(`/superadmin/admins/${activateModal.admin.id}/set-active`, { active: true, active_until: iso });
      toast.success(`Activated until ${new Date(iso).toLocaleString()}`);
      setActivateModal(null);
      refetchAdmins();
    } catch (err) { toast.error(formatError(err)); }
  };
  const doResetPw = async () => {
    try { await api.post(`/superadmin/admins/${resetPw.admin.id}/reset-password`, { new_password: resetPw.pw }); toast.success("Password reset"); setResetPw({ admin: null, pw: "" }); }
    catch (e) { toast.error(formatError(e)); }
  };

  const viewAdminCode = async (a) => {
    try {
      const { data } = await api.get(`/superadmin/admins/${a.id}/login-code`);
      setShowCode((s) => ({ ...s, [a.id]: { ...data, visible: true } }));
    } catch (e) { toast.error(formatError(e)); }
  };
  const rotateAdminCode = async (a) => {
    try {
      const { data } = await api.post(`/superadmin/admins/${a.id}/rotate-code`);
      setShowCode((s) => ({ ...s, [a.id]: { ...data, visible: true } }));
      toast.success(`New code: ${data.code}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveSaCode = async () => {
    if (!newSaCode || newSaCode.length < 4) { toast.error("Code must be at least 4 chars"); return; }
    try {
      const { data } = await api.post("/superadmin/login-code", { code: newSaCode });
      setMyCode({ code: data.code, rotated_at: data.rotated_at, visible: true });
      setNewSaCode("");
      toast.success("SuperAdmin access code updated");
    } catch (e) { toast.error(formatError(e)); }
  };

  const doLogout = () => { logout(); nav(SUPERADMIN_PATH); };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 z-30 bg-background">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-primary grid place-items-center"><Coins className="h-4 w-4 text-primary-foreground" /></div>
            <div>
              <div className="font-display font-semibold">TossKing · SuperAdmin</div>
              <div className="text-xs text-muted-foreground">Signed in as {user?.username}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="outline" onClick={doLogout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" />My Access Code</CardTitle>
            <CardDescription>Used at login alongside your username & password.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="border border-border rounded px-3 py-2 mono text-lg" data-testid="sa-code">
                {myCode.visible ? myCode.code : "••••••••"}
              </div>
              <Button size="sm" variant="ghost" onClick={()=>setMyCode({...myCode, visible: !myCode.visible})} data-testid="sa-code-toggle">
                {myCode.visible ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
              </Button>
              <div className="text-xs text-muted-foreground">Last updated {fmtDT(myCode.rotated_at)}</div>
            </div>
            <div className="flex gap-2 max-w-md">
              <Input value={newSaCode} onChange={(e)=>setNewSaCode(e.target.value.toUpperCase())} placeholder="New code (min 4 chars)" data-testid="sa-new-code" className="mono tracking-widest" />
              <Button onClick={saveSaCode} data-testid="sa-save-code">Update code</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4" />Create Admin / Brand</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="create-admin-form">
              <div><Label>Username</Label><Input required value={form.username} onChange={(e)=>setForm({...form, username:e.target.value})} data-testid="new-admin-username" /></div>
              <div><Label>Password</Label><Input required value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} data-testid="new-admin-password" /></div>
              <div><Label>Brand name</Label><Input required value={form.brand_name} onChange={(e)=>setForm({...form, brand_name:e.target.value})} data-testid="new-brand-name" /></div>
              <div><Label>Brand slug</Label><Input required value={form.brand_slug} onChange={(e)=>setForm({...form, brand_slug:e.target.value.toLowerCase()})} data-testid="new-brand-slug" placeholder="e.g. royals" /></div>
              <div className="flex items-end"><Button type="submit" className="w-full btn-push" data-testid="create-admin-btn">Create</Button></div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Admins & Brands</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active until</TableHead>
                  <TableHead>Access Code</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((a)=>{
                  const codeInfo = showCode[a.id];
                  return (
                    <TableRow key={a.id} data-testid={`admin-row-${a.username}`}>
                      <TableCell className="font-semibold">{a.username}</TableCell>
                      <TableCell>{a.brand_name}</TableCell>
                      <TableCell className="mono text-xs">{a.brand_slug}</TableCell>
                      <TableCell>{a.is_active ? <Badge>Active</Badge> : <Badge variant="destructive">Deactivated</Badge>}</TableCell>
                      <TableCell className="mono text-xs" data-testid={`active-until-${a.username}`}>
                        {a.active_until ? fmtDT(a.active_until) : (a.is_active ? "—" : "—")}
                      </TableCell>
                      <TableCell>
                        {codeInfo?.visible ? (
                          <div className="mono text-xs" data-testid={`code-${a.username}`}>
                            {codeInfo.code}
                            <div className="text-[10px] text-muted-foreground">{fmtDT(codeInfo.rotated_at)}</div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={()=>viewAdminCode(a)} data-testid={`view-code-${a.username}`}><Eye className="h-3 w-3 mr-1"/>View</Button>
                        )}
                      </TableCell>
                      <TableCell className="space-x-2 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={()=>rotateAdminCode(a)} data-testid={`rotate-code-${a.username}`}><RefreshCw className="h-3 w-3 mr-1"/>Rotate code</Button>
                        <Dialog>
                          <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`reset-pw-${a.username}`} onClick={()=>setResetPw({admin:a, pw:""})}><KeyRound className="h-3 w-3 mr-1" />Reset PW</Button></DialogTrigger>
                          <DialogContent>
                            <DialogHeader><DialogTitle>Reset password for {a.username}</DialogTitle></DialogHeader>
                            <Input placeholder="New password" value={resetPw.pw} onChange={(e)=>setResetPw({...resetPw, pw:e.target.value})} data-testid="reset-pw-input" />
                            <DialogFooter><Button onClick={doResetPw} data-testid="reset-pw-confirm">Reset</Button></DialogFooter>
                          </DialogContent>
                        </Dialog>
                        {a.is_active ? (
                          <Button size="sm" variant="destructive" onClick={()=>setActive(a, false)} data-testid={`deactivate-${a.username}`}><ShieldOff className="h-3 w-3 mr-1" />Deactivate</Button>
                        ) : (
                          <Button size="sm" onClick={()=>setActive(a, true)} data-testid={`activate-${a.username}`}><ShieldCheck className="h-3 w-3 mr-1" />Activate</Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={()=>{ setDelAdmin(a); setDelConfirm(""); }} data-testid={`delete-admin-${a.username}`}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {admins.length===0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No admins yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      <Dialog open={!!activateModal} onOpenChange={(o)=>!o&&setActivateModal(null)}>
        <DialogContent data-testid="activate-admin-dialog">
          {activateModal && (
            <>
              <DialogHeader><DialogTitle>Activate admin · {activateModal.admin.username}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Pick the date and time until which <b>{activateModal.admin.brand_name}</b> should stay active. The admin (and their users) will be auto-deactivated at that moment.
                </div>
                <div>
                  <Label>Active until</Label>
                  <DateTimePicker
                    value={activateModal.until}
                    onChange={(v)=>setActivateModal({...activateModal, until: v})}
                    testId="activate-until"
                    placeholder="Pick date & time"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    ["+1 day", 24*60],
                    ["+7 days", 7*24*60],
                    ["+30 days", 30*24*60],
                    ["+90 days", 90*24*60],
                  ].map(([label, mins])=>(
                    <button
                      key={label} type="button"
                      onClick={()=>{
                        const d = new Date(Date.now() + mins*60*1000);
                        const pad = (n) => String(n).padStart(2, "0");
                        const s = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                        setActivateModal({...activateModal, until: s});
                      }}
                      data-testid={`activate-quick-${label}`}
                      className="px-3 py-1 rounded-full border border-border text-xs font-semibold bg-card hover:border-primary hover:bg-primary/10 btn-push"
                    >{label}</button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setActivateModal(null)} data-testid="activate-cancel">Cancel</Button>
                <Button onClick={submitActivate} data-testid="activate-confirm">Activate</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delAdmin} onOpenChange={(o)=>{ if(!o){ setDelAdmin(null); setDelConfirm(""); } }}>
        <DialogContent data-testid="delete-admin-dialog">
          {delAdmin && (
            <>
              <DialogHeader><DialogTitle>Delete admin · {delAdmin.username}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
                  This will permanently delete the admin, their brand, and <b>all associated users, events, bets, transactions, withdrawals, and announcements</b>. This action cannot be undone.
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Username</span><span className="font-semibold">{delAdmin.username}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Brand</span><span>{delAdmin.brand_name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Brand slug</span><span className="mono">{delAdmin.brand_slug}</span></div>
                </div>
                <div>
                  <Label>Type <span className="mono font-bold">{delAdmin.brand_slug}</span> to confirm</Label>
                  <Input value={delConfirm} onChange={(e)=>setDelConfirm(e.target.value)} data-testid="delete-admin-confirm-input" autoFocus />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>{ setDelAdmin(null); setDelConfirm(""); }} data-testid="delete-admin-cancel">Cancel</Button>
                <Button variant="destructive" onClick={deleteAdmin} disabled={delConfirm.trim() !== delAdmin.brand_slug} data-testid="delete-admin-confirm">Delete permanently</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
